import { useTheme } from "@emotion/react";
import React, { useState } from "react";
import { MdAdd, MdEdit } from "react-icons/md";
import { useQueryClient } from "react-query";

import {
  TicketStatus,
  type TicketCategory,
  type TicketCustomerLinkDto,
  type TicketDetailDto,
} from "@megaron/support-contracts";
import { useClientManager } from "@megaron/react-clients";
import { Uuid } from "@megaron/uuid";
import { Button } from "@dash/form";

import { SupportTicketProductsEditField } from "../products/SupportTicketProductsField";
import { SupportEditTagsDialog } from "../shared/SupportEditTagsDialog";
import { SupportFieldLabel } from "../shared/SupportFieldLabel";
import { SupportTicketTags } from "../shared/SupportTicketTags";
import { useSupportTicketEditable } from "../shared/useSupportTicketEditable";
import { SupportTicketCustomersSection } from "./SupportTicketCustomersSection";

type Props = {
  ticketId: Uuid;
  subject: string;
  status: TicketStatus;
  priority: "low" | "medium" | "high" | "urgent" | null;
  category: TicketCategory | null;
  invoiceNumber: string | null;
  orderNumber: string | null;
  reporterName: string;
  reporterEmail: string;
  reporterPhone: string | null;
  ticketCustomers: TicketCustomerLinkDto[];
  tags: string[];
  products: TicketDetailDto["products"];
};

export const SupportTicketContactCrmCard: React.FC<Props> = ({
  ticketId,
  subject,
  status,
  priority,
  category,
  invoiceNumber,
  orderNumber,
  reporterName,
  reporterEmail,
  reporterPhone,
  ticketCustomers,
  tags,
  products,
}) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [tagsDialogOpen, setTagsDialogOpen] = useState(false);
  const isEditable = useSupportTicketEditable(status);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["support", "getTicket"] });

  const reporterMutation = useClientManager("support").updateTicket().useMutation({ onSuccess: invalidate });

  const handleSaveReporter = (values: { reporterName: string; reporterEmail: string; reporterPhone: string }) => {
    reporterMutation.mutate({
      id: ticketId,
      subject,
      status,
      priority,
      category,
      tags,
      invoiceNumber,
      orderNumber,
      reporterName: values.reporterName,
      reporterEmail: values.reporterEmail,
      reporterPhone: values.reporterPhone,
    });
  };

  const sectionGap = "1rem";
  const hasTags = tags.length > 0;

  const tagsSection = (
    <div css={{ display: "flex", flexDirection: "column", gap: "0.375rem" }}>
      <SupportFieldLabel>Tagi</SupportFieldLabel>
      <div css={{ display: "flex", gap: "7px", alignItems: "center", color: theme.colors.primary, flexWrap: "wrap" }}>
        <SupportTicketTags tags={tags} />
        <Button
          variant={hasTags ? "text" : "outline"}
          size="small"
          icon={hasTags ? <MdEdit size={18} /> : <MdAdd size={16} />}
          onClick={() => setTagsDialogOpen(true)}
          isDisabled={!isEditable}
          css={{
            flexShrink: 0,
            ...(hasTags ? { padding: "0.125rem" } : {}),
          }}
        >
          {hasTags ? "Edytuj" : "Dodaj tag"}
        </Button>
      </div>
    </div>
  );

  const tagsDialog = tagsDialogOpen ? (
    <SupportEditTagsDialog
      onClose={() => setTagsDialogOpen(false)}
      ticketId={ticketId}
      subject={subject}
      status={status}
      priority={priority}
      category={category}
      invoiceNumber={invoiceNumber}
      orderNumber={orderNumber}
      ticketTags={tags}
      onSuccess={invalidate}
    />
  ) : null;

  return (
    <>
      <div css={{ display: "flex", flexDirection: "column" }}>
        {tagsSection}
        <div css={{ marginTop: sectionGap }}>
          <SupportTicketCustomersSection
            ticketId={ticketId}
            reporterName={reporterName}
            reporterEmail={reporterEmail}
            reporterPhone={reporterPhone}
            ticketCustomers={ticketCustomers}
            isEditable={isEditable}
            isSavingReporter={reporterMutation.isLoading}
            onSaveReporter={handleSaveReporter}
          />
        </div>
        <div css={{ marginTop: sectionGap }}>
          <SupportTicketProductsEditField ticketId={ticketId} products={products} isDisabled={!isEditable} />
        </div>
      </div>
      {tagsDialog}
    </>
  );
};
